import { keepPreviousData } from "@tanstack/react-query";

import { useApiClient } from "@/src/hooks/useApiClient";
import { useQueryErrorToast } from "@/src/hooks/useQueryErrorToast";
import { useTranslations } from "@/src/i18n/i18n";
import { ApiError } from "@/src/libs/ApiError";

/**
 * Taille de la page de messages.
 *
 * Cinquante : de quoi lire un échange entier sans faire de la fenêtre un export
 * de conversation. Le serveur plafonne à cent et renvoie `hasMoreBefore`, donc
 * ce qui manque se **voit** — il n'est pas tronqué en silence.
 */
const PAGE = 50;

/**
 * Une discussion et sa dernière page de messages.
 *
 * ⚠️ **`isEnabled` n'est pas une optimisation.** La requête ne doit partir qu'à
 * l'ouverture de la fenêtre : la déclencher au rendu de la liste ferait
 * consulter à la modération trente discussions que personne n'a ouvertes — et
 * le serveur **journalise chaque lecture**, donc ces trente-là laisseraient
 * autant de traces mensongères dans le registre.
 *
 * Même motif que `useReportContext`, avec une raison de plus.
 */
export function useAdminConversation(
  conversationId: number,
  isEnabled: boolean,
  before?: number,
) {
  const $api = useApiClient();
  const t = useTranslations("conversations");

  const { data, error, isError, isLoading, isFetching } = $api.useQuery(
    "get",
    "/api/v1/moderation/conversations/{id}",
    {
      params: { path: { id: conversationId }, query: { before, limit: PAGE } },
    },
    {
      enabled: isEnabled && conversationId > 0,
      /*
        ⚠️ Sans `keepPreviousData`, remonter le fil **vide la fenêtre**.
        `before` fait partie de la clé de requête : la page suivante est une
        requête neuve, donc sans données en cache, donc `isLoading` repasse à
        vrai et le squelette remplace les messages qu'on était en train de lire.
        Ici la page précédente reste à l'écran, et `isFetching` signale
        l'attente sur le bouton.
      */
      placeholderData: keepPreviousData,
    },
  );

  // Un 404 est un cas normal : la discussion a été supprimée depuis — avec son
  // événement, par exemple. La fenêtre le dit elle-même ; un toast ferait
  // croire à une panne.
  const isMissing = ApiError.from(error)?.status === 404;

  useQueryErrorToast(isError, t("load-error"), error, !isMissing);

  return {
    conversation: data?.data,
    isLoading,
    isFetching,
    isError,
    isMissing,
  };
}
